import React, { useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { confirmSignUp as amplifyConfirmSignUp, AuthError } from 'aws-amplify/auth';
import PianoBackground from '../../assets/images/pianoBackground.png';
import Logo from 'Components/Logo';
import { CustomCircularProgress } from 'Components/StyledComponents';
import CheckIcon from '@mui/icons-material/Check';
import { handleAuthErrors } from 'Utils/Forms';
import * as authActions from 'Actions/auth';
import * as eventActions from 'Actions/events';
import { AppDispatch } from 'Reducers';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';

const Verification: React.FC = () => {
  const [verified, setVerified] = React.useState(false);
  const [error, setError] = React.useState('');
  const [isConfirming, setIsConfirming] = React.useState(true);
  const [verifiedEmail, setVerifiedEmail] = React.useState<string | undefined>(undefined);

  const confirmStarted = React.useRef(false);
  const { user, tempSignUpPass, logInUsername, processPending, setPendingEmailOrPhone } =
    useAuthWrapperContext();

  const navigate = useNavigate();
  const search = useLocation().search;
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (user) {
      navigate('/roadmap');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (confirmStarted.current) return;
    confirmStarted.current = true;

    const query = new URLSearchParams(search);
    const username = query.get('user_name');
    const confirmationCode = query.get('confirmation_code');
    const emailParam = query.get('email');

    if (username && confirmationCode && emailParam) {
      const normalizedEmail = emailParam.replace(/ /g, '+');
      setVerifiedEmail(normalizedEmail);

      amplifyConfirmSignUp({
        username,
        confirmationCode,
      })
        .then(() => {
          setVerified(true);
          dispatch(eventActions.accountVerifiedAction());
        })
        .catch((rawError: Error) => {
          const authError = rawError as unknown as AuthError;
          const alreadyConfirmed =
            authError.name === 'NotAuthorizedException' &&
            String(authError.message || '').includes('Current status is CONFIRMED');
          if (alreadyConfirmed) {
            setVerified(true);
            dispatch(eventActions.accountVerifiedAction());
            return;
          }

          const err = handleAuthErrors(authError);
          dispatch(authActions.setTempSignUpPass(undefined));
          if (err?.name) setError(err.name);
          if (err?.unknown) setError(err.unknown);
        })
        .finally(() => {
          setIsConfirming(false);
        });
    } else {
      setIsConfirming(false);
      navigate('/signin');
    }
  }, [dispatch, navigate, search]);

  const handleContinue = () => {
    const storedPass = tempSignUpPass || localStorage.getItem('tempSignUpPass') || undefined;

    if (verifiedEmail && storedPass) {
      logInUsername(verifiedEmail, storedPass, '');
      navigate('/roadmap');
      return;
    }

    if (verifiedEmail) {
      setPendingEmailOrPhone?.(verifiedEmail);
    }
    navigate('/signin');
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <img
        style={{
          height: '100vh',
          opacity: 0.3,
          position: 'absolute',
        }}
        src={PianoBackground}
        alt="piano-background"
      ></img>
      <Box className="page-div-styles">
        <Box className="filter-div-styles">
          <Box className="sign-in-div-styles">
            <Logo />
            <Box className="desc-container welcome-container">
              <Typography variant="h3" className="title-welcome" style={{ marginTop: '50px' }}>
                {!verified && !error && 'Verification Pending'}
                {verified && 'Email Verified'}
                {error && 'Verification Error'}
              </Typography>
              {((!verified && !error && isConfirming) || processPending) && (
                <CustomCircularProgress />
              )}
              {verified && !error && !processPending && !isConfirming && (
                <CheckIcon color="success" sx={{ height: '80px', width: '80px' }} />
              )}
              {error && <Typography color="red">{error}</Typography>}
              {verified && !error && !processPending && !isConfirming && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography sx={{ marginTop: '20px', marginBottom: '14px' }} color="#00C2FF">
                    {tempSignUpPass || localStorage.getItem('tempSignUpPass')
                      ? 'Click continue to enter the app without signing in again.'
                      : 'Your email is verified. Continue to sign in.'}
                  </Typography>
                  <Button variant="contained" sx={{ minWidth: '220px' }} onClick={handleContinue}>
                    Continue
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Verification;
